"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGetReview = void 0;
var _auth = require("@ori/auth");
var _environment = require("@ori/environment");
var _fetching = require("@ori/fetching");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _i18n = require("@ori/i18n");
var _presentationUtils = require("@ori/presentation-utils");
var _productBox = require("@ori/product-box");
var _staticApiClientReact = require("@ori/static-api-client-react");
var _react = require("react");
var _constants = require("../constants");
var _contexts = require("../contexts");
// istanbul ignore file @preserve -- api

const useGetReview = () => {
  const {
    isAnonymousUser,
    isUserVerified,
    customerId
  } = (0, _auth.useToken)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    productCode: productCodeFromEvent,
    reviewId
  } = (0, _contexts.useRatingAndReviewDialog)();
  const {
    reset
  } = (0, _contexts.useRatingAndReviewForm)();

  // Fetch customer profile data to prefill author name if new review is created
  const {
    data: customerProfileData,
    isLoading: isCustomerProfileLoading,
    isValidating: isCustomerProfileValidating
  } = (0, _globalApiClientReact.useGetCustomerProfile)(customerId, {
    [_constants.PROCESS_CONTEXT]: 'Profile',
    [_fetching.TENANT_CONTEXT]: tenant
  }, {
    swr: {
      ...(0, _presentationUtils.getRetryErrorSwrOptions)(),
      enabled: isUserVerified && !isAnonymousUser && !reviewId
    }
  });
  // Fetch review data if reviewId is provided
  const {
    data,
    isLoading,
    isValidating
  } = (0, _globalApiClientReact.useGetApplicationsCustomerFeedbackCustomerReview)(String(customerId), String(reviewId), {
    [_constants.ACCEPT_LANGUAGE]: language,
    [_fetching.TENANT_CONTEXT]: tenant
  }, {
    swr: {
      ...(0, _presentationUtils.getRetryErrorSwrOptions)(),
      revalidateIfStale: true,
      dedupingInterval: 5000,
      enabled: isUserVerified && !isAnonymousUser && !!reviewId
    }
  });
  const {
    productCode: productCodeFromData = ''
  } = data ?? {};
  const productCode = (reviewId ? productCodeFromData : productCodeFromEvent) ?? '';
  // Fetch data for review productCode if review is provided or use event productCode
  // if new review is created
  const {
    data: productBoxData,
    isLoading: isProductLoading,
    isValidating: isProductValidating
  } = (0, _staticApiClientReact.useGetTenantApplicationsProductBox)(tenant, {
    lang: language,
    productCodes: [productCode]
  }, {
    swr: {
      ...(0, _presentationUtils.getRetryErrorSwrOptions)(),
      enabled: !!productCode
    }
  });
  const product = productBoxData?.data?.products[0];
  const {
    imageUrl: productImageUrl,
    concept,
    shadeName
  } = product ?? {};
  const shade = product?.concept?.products.find(s => s.productCode === productCode);
  const {
    brand,
    name: productName
  } = concept ?? {};
  const {
    name: productBrandName
  } = brand ?? {};
  const {
    hexColors,
    imageUrl: shadeImageUrl
  } = shade ? (0, _productBox.mapShade)(shade) : {
    hexColors: undefined,
    imageUrl: undefined
  };
  const currentShade = (0, _react.useMemo)(() => ({
    canBeSold: true,
    hexColors,
    imageUrl: shadeImageUrl,
    name: shadeName,
    productCode
  }), [hexColors, productCode, shadeImageUrl, shadeName]);
  (0, _react.useEffect)(() => {
    if (reviewId && data) {
      const {
        author,
        cons,
        productCode: dataProductCode,
        pros,
        reviewText,
        score
      } = data;
      reset({
        author,
        cons,
        productCode: dataProductCode,
        pros,
        reviewText,
        score
      });
    } else if (productCodeFromEvent && customerProfileData) {
      const author = [customerProfileData.firstName, customerProfileData.lastName].filter(Boolean).join(' ');
      reset({
        author,
        cons: [],
        productCode: productCodeFromEvent,
        pros: [],
        reviewText: '',
        score: 0
      });
    }
  }, [customerProfileData, customerProfileData?.firstName, customerProfileData?.lastName, data, productCodeFromEvent, reset, reviewId]);
  return (0, _react.useMemo)(() => ({
    data,
    currentShade,
    isMultiShade: !!shadeName,
    loading: isLoading || isValidating || isProductLoading || isProductValidating || isCustomerProfileLoading || isCustomerProfileValidating,
    productCode,
    productBrandName,
    productImageUrl,
    productName
  }), [currentShade, data, isCustomerProfileLoading, isCustomerProfileValidating, isLoading, isProductLoading, isProductValidating, isValidating, productBrandName, productCode, productImageUrl, productName, shadeName]);
};
exports.useGetReview = useGetReview;
